import * as React from 'react';
import { Hit } from 'react-instantsearch-core';
import SpriteIcon from 'search/components/sprite-icon';

function isSalsifyImageUrl(imageUrl: string): boolean {
  return imageUrl.indexOf('//images.salsify.com/') !== -1;
}

function transformMainImageUrl(imageUrl: string): string {
  return imageUrl.replace('w_772,h_320,c_fill,g_north', 'w_772,h_320,c_lpad,g_north');
}

// Mirror of the PHP symmons_salsify_add_url_args(): inject a Salsify (Cloudinary)
// transformation segment before the filename, after any signature segment.
function salsifyAddUrlArgs(url: string, args: string[]): string {
  if (!args.length) {
    return url;
  }
  const parts = url.split('/');
  parts.push(parts[parts.length - 1]);
  parts[parts.length - 2] = args.join(',');
  return parts.join('/');
}

// Mirror of the PHP symmons_salsify_attachment_arg() + get_download_url(): build a
// direct-to-Salsify download URL that renames the file on the fly via the CDN's
// fl_attachment flag, so we link straight to the CDN instead of the /download_asset/ proxy.
// Commas and slashes are structural in the transformation segment, so they are replaced. The
// extension is re-appended by the CDN, so a trailing extension matching `format` is stripped to
// avoid double extensions (e.g. "file.jpg.jpg"); non-extension dots (e.g. "1.5") are left intact.
function salsifyDownloadUrl(salsifyUrl: string, name: string, format: string): string {
  let filename = name.replace(/[,/]/g, '-');
  if (format && filename.toLowerCase().endsWith(`.${format.toLowerCase()}`)) {
    filename = filename.slice(0, -(format.length + 1));
  }
  return salsifyAddUrlArgs(salsifyUrl, [`fl_attachment:${filename}`]);
}

export interface DocumentHitInterface {
  name: string;
  salsify_id: string;
  salsify_url: string;
  types: string[];
  thumbnail: string;
  salsify_format: string;
}

interface DocumentHitProps {
  hit: Hit<DocumentHitInterface>;
}

class DocumentHit extends React.Component<DocumentHitProps> {
  render() {
    const { name, salsify_id, salsify_url, types, thumbnail, salsify_format } = this.props.hit;

    const thumbnailUrl =
      types.includes('Main Image') && isSalsifyImageUrl(thumbnail) ? transformMainImageUrl(thumbnail) : thumbnail;

    // Link straight to Salsify with an fl_attachment rename when we have the CDN URL; fall back to
    // the legacy /download_asset/ proxy path (which now 301-redirects) only if it is missing.
    const download_url = salsify_url
      ? salsifyDownloadUrl(salsify_url, name, salsify_format)
      : `/download_asset/?id=${salsify_id}`;

    return (
      <>
        <figure
          className="c-card__figure c-card__figure--fixed-height"
          style={{ backgroundImage: `url(${thumbnailUrl})` }}
        >
          <a href={download_url} target="_blank" rel="noreferrer">
            <img src={thumbnailUrl} alt={name} />
          </a>
          <div className="c-card__tags">
            <span className="c-tag c-tag--small">{types.join(', ')}</span>
          </div>
        </figure>
        <div className="c-card__body">
          <header className="c-card__header">
            <h4 className="c-card__title">
              <a href={download_url} target="_blank" rel="noreferrer" className="c-card__link">
                {name}
              </a>
            </h4>
          </header>
          <div className="c-card__desc">
            <p className="c-note">{salsify_format.toUpperCase()}</p>
          </div>
          <footer className="c-card__footer">
            <p>
              <a href={download_url} target="_blank" rel="noreferrer" className="c-link-cta-basic c-link-cta--small">
                <SpriteIcon name="download" classes={['o-svg-icon']} />
                <span>Download</span>
              </a>
            </p>
          </footer>
        </div>
      </>
    );
  }
}

export default DocumentHit;
